import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Card, CardActionArea,
  InputAdornment, CircularProgress, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ProjectService from '../services/ProjectService';
import UserService from '../services/UserService';
import CustomerService from '../services/CustomerService';
import ReportCompositionDrawer from './ReportCompositionDrawer';
import Layout from './Layout';
import { useToast } from '../contexts/ToastContext';

// ─── Config ───────────────────────────────────────────────────────────────────

const PROJECT_STATUS = {
  IN_PROGRESS: { label: 'In Progress', color: '#2563EB', bg: '#EFF6FF' },
  PENDING:     { label: 'Pending',     color: '#D97706', bg: '#FFFBEB' },
  COMPLETED:   { label: 'Completed',   color: '#16A34A', bg: '#F0FDF4' },
  ON_HOLD:     { label: 'On Hold',     color: '#64748B', bg: '#F1F5F9' },
};

const REPORT_STATUS = {
  DRAFT:                      { label: 'Draft',            color: '#D97706', bg: '#FFFBEB' },
  SIGNED:                     { label: 'Signed',           color: '#16A34A', bg: '#F0FDF4' },
  PUBLISHED:                  { label: 'Published',        color: '#16A34A', bg: '#F0FDF4' },
  REFUSED:                    { label: 'Refused',          color: '#DC2626', bg: '#FEF2F2' },
  CUSTOMER_NOT_AVAILABLE:     { label: 'Not Available',    color: '#EA580C', bg: '#FFF7ED' },
  AWAITING_REMOTE_SIGNATURE:  { label: 'Awaiting Sig.',    color: '#2563EB', bg: '#EFF6FF' },
  CONFIRMED_WITHOUT_SIGNATURE:{ label: 'Confirmed',        color: '#16A34A', bg: '#F0FDF4' },
};

const STATUS_FILTERS = [
  { value: 'ALL',    label: 'All' },
  { value: 'DRAFT',  label: 'Draft' },
  { value: 'SIGNED', label: 'Signed' },
  { value: 'CONFIRMED_WITHOUT_SIGNATURE', label: 'Confirmed' },
  { value: 'REFUSED', label: 'Refused' },
];

const PDF_STATUSES = new Set(['SIGNED', 'PUBLISHED', 'CONFIRMED_WITHOUT_SIGNATURE']);

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatTime(d) {
  if (!d) return null;
  return new Date(d).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
}

// ─── Status pill ──────────────────────────────────────────────────────────────

function StatusPill({ statusMap, value, size = 'md' }) {
  const cfg = statusMap[value] || { label: value || '—', color: '#64748B', bg: '#F1F5F9' };
  const px = size === 'sm' ? 0.75 : 1;
  const py = size === 'sm' ? 0.15 : 0.3;
  const fs = size === 'sm' ? 10 : 11;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: cfg.bg, borderRadius: '20px', px, py, flexShrink: 0 }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: cfg.color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: fs, fontWeight: 600, color: cfg.color, lineHeight: 1, whiteSpace: 'nowrap' }}>
        {cfg.label}
      </Typography>
    </Box>
  );
}

// ─── Report card ──────────────────────────────────────────────────────────────

function ReportCard({ report, currentUserId, onResume, onViewPdf, onDownloadPdf, onEmail, pdfViewLoading, pdfDownloadLoading }) {
  const cfg = REPORT_STATUS[report.status] || { color: '#94A3B8' };
  const hasPdf = PDF_STATUSES.has(report.status);
  const isOwn = String(report.createdBy) === String(currentUserId);

  return (
    <Box sx={{
      bgcolor: '#fff',
      borderRadius: '10px',
      boxShadow: '0 1px 3px rgba(15,23,42,0.07), 0 1px 2px rgba(15,23,42,0.04)',
      borderLeft: `3px solid ${cfg.color}`,
      overflow: 'hidden',
      transition: 'box-shadow 0.15s',
      '&:hover': { boxShadow: '0 4px 16px rgba(15,23,42,0.09), 0 2px 4px rgba(15,23,42,0.05)' },
    }}>
      <Box sx={{ px: 2, pt: 1.75, pb: 1.5 }}>
        {/* Top row: name + status */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 0.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0F172A', lineHeight: 1.3 }}>
              {report.displayName || report.name || `Report #${report.id}`}
            </Typography>
          </Box>
          <StatusPill statusMap={REPORT_STATUS} value={report.status} />
        </Box>

        {/* Meta row */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: report.workDescription || report.signerName ? 1 : 0 }}>
          <Typography component="span" sx={{ fontSize: 12, color: '#94A3B8' }}>
            {formatDate(report.reportDate)}
            {formatTime(report.createdAt) ? ` · ${formatTime(report.createdAt)}` : ''}
          </Typography>
          {isOwn && (
            <Typography component="span" sx={{ fontSize: 12, color: '#94A3B8' }}>· by you</Typography>
          )}
          {report.signerName && (
            <Typography component="span" sx={{ fontSize: 12, color: '#64748B' }}>
              · signed by <strong style={{ color: '#475569' }}>{report.signerName}</strong>
              {report.signedAt ? ` on ${formatDate(report.signedAt)}` : ''}
            </Typography>
          )}
          {report.emailSentAt && (
            <Tooltip title={`Sent by ${report.emailSentByName || 'unknown'}`}>
              <Typography component="span" sx={{ fontSize: 12, color: '#16A34A', display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                · <EmailIcon sx={{ fontSize: 11 }} /> emailed {formatDate(report.emailSentAt)}
              </Typography>
            </Tooltip>
          )}
        </Box>

        {/* Work description snippet */}
        {report.workDescription && (
          <Typography sx={{
            fontSize: 12, color: '#64748B', mb: 1,
            overflow: 'hidden', textOverflow: 'ellipsis',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {report.workDescription}
          </Typography>
        )}

        {/* Actions row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25, mt: 0.5 }}>
          {report.status === 'DRAFT' && (
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 13 }} />}
              onClick={onResume}
              sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, color: '#427898', px: 1.25, py: 0.5, borderRadius: 1.5, '&:hover': { bgcolor: '#EFF6FF' } }}
            >
              Resume
            </Button>
          )}
          {hasPdf && (
            <>
              <Tooltip title="View PDF" placement="top">
                <IconButton size="small" onClick={onViewPdf} disabled={pdfViewLoading}
                  sx={{ color: '#427898', '&:hover': { bgcolor: '#EFF6FF' }, width: 30, height: 30 }}>
                  {pdfViewLoading ? <CircularProgress size={14} sx={{ color: '#427898' }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Download PDF" placement="top">
                <IconButton size="small" onClick={onDownloadPdf} disabled={pdfDownloadLoading}
                  sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' }, width: 30, height: 30 }}>
                  {pdfDownloadLoading ? <CircularProgress size={14} sx={{ color: '#64748B' }} /> : <DownloadIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Send by email" placement="top">
                <IconButton size="small" onClick={onEmail}
                  sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' }, width: 30, height: 30 }}>
                  <EmailIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Project panel ─────────────────────────────────────────────────────────────

function ProjectPanel({ project, onBack, currentUser }) {
  const { showToast } = useToast();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  // PDF download
  const [pdfDownloadLoadingId, setPdfDownloadLoadingId] = useState(null);
  // PDF quick view
  const [pdfViewUrl, setPdfViewUrl] = useState(null);
  const [pdfViewName, setPdfViewName] = useState('');
  const [pdfViewLoadingId, setPdfViewLoadingId] = useState(null);

  // Email dialog
  const [emailDialog, setEmailDialog] = useState(null);
  const [emailContacts, setEmailContacts] = useState([]);
  const [emailContactsLoading, setEmailContactsLoading] = useState(false);
  const [emailSelectedIds, setEmailSelectedIds] = useState([]);
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailNewForm, setEmailNewForm] = useState(null);
  const [emailNewErrors, setEmailNewErrors] = useState({});
  const [emailNewSaving, setEmailNewSaving] = useState(false);

  const loadReports = useCallback(() => {
    setLoadingReports(true);
    ProjectService.getProject(0, 1, project.id)
      .then(data => setReports((data.reports || []).slice().reverse()))
      .catch(() => showToast('Failed to load reports', 'error'))
      .finally(() => setLoadingReports(false));
  }, [project.id]); // eslint-disable-line

  useEffect(() => { loadReports(); }, [loadReports]);

  // Filtering
  const visibleReports = reports.filter(r => {
    if (statusFilter !== 'ALL' && r.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (r.displayName || '').toLowerCase().includes(q)
        || (r.signerName || '').toLowerCase().includes(q)
        || (r.workDescription || '').toLowerCase().includes(q);
    }
    return true;
  });

  // Status filter counts
  const countByStatus = reports.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  // Handlers
  function openNewReport() { setEditingReport(null); setDrawerOpen(true); }
  function openEditReport(r) { setEditingReport(r); setDrawerOpen(true); }

  async function handleDownloadPdf(reportId) {
    setPdfDownloadLoadingId(reportId);
    try {
      const response = await ProjectService.downloadPdfReport(reportId);
      const blob = response?.data instanceof Blob ? response.data : response;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `report-${reportId}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      showToast('PDF not available yet', 'warning');
    } finally {
      setPdfDownloadLoadingId(null);
    }
  }

  async function handleQuickViewPdf(report) {
    setPdfViewLoadingId(report.id);
    try {
      const response = await ProjectService.downloadPdfReport(report.id);
      const blob = response?.data instanceof Blob ? response.data : response;
      setPdfViewName(report.displayName || `Report #${report.id}`);
      setPdfViewUrl(URL.createObjectURL(blob));
    } catch {
      showToast('PDF not available yet', 'warning');
    } finally {
      setPdfViewLoadingId(null);
    }
  }

  function closePdfView() {
    if (pdfViewUrl) URL.revokeObjectURL(pdfViewUrl);
    setPdfViewUrl(null); setPdfViewName('');
  }

  function openEmailDialog(report) {
    setEmailSelectedIds([]);
    setEmailNewForm(null); setEmailNewErrors({});
    setEmailBody(`Dear Customer,\n\nPlease find attached the service report ${report.displayName || ''} for your records.\n\nBest regards`);
    setEmailDialog({ reportId: report.id, displayName: report.displayName });
    if (project.customer?.id) {
      setEmailContactsLoading(true);
      CustomerService.getContactsByCustomer(project.customer.id)
        .then(list => setEmailContacts(list || []))
        .catch(() => setEmailContacts([]))
        .finally(() => setEmailContactsLoading(false));
    }
  }

  function toggleEmailContact(id) {
    setEmailSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  function validateEmailNewForm(form) {
    const e = {};
    if (!form.firstName?.trim()) e.firstName = 'Required';
    if (!form.lastName?.trim()) e.lastName = 'Required';
    if (!form.email?.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    if (!form.phone?.trim()) e.phone = 'Required';
    return e;
  }

  async function handleAddEmailContact() {
    const errs = validateEmailNewForm(emailNewForm);
    if (Object.keys(errs).length) { setEmailNewErrors(errs); return; }
    setEmailNewSaving(true);
    try {
      const created = await CustomerService.createContact({
        customer: project.customer.id,
        firstName: emailNewForm.firstName.trim(), lastName: emailNewForm.lastName.trim(),
        email: emailNewForm.email.trim(), phone: emailNewForm.phone.trim(),
      });
      setEmailContacts(prev => [...prev, created]);
      setEmailSelectedIds(prev => [...prev, created.id]);
      setEmailNewForm(null); setEmailNewErrors({});
    } catch { showToast('Failed to create contact', 'error'); }
    finally { setEmailNewSaving(false); }
  }

  async function handleSendEmail() {
    const to = emailContacts.filter(c => emailSelectedIds.includes(c.id)).map(c => c.email).filter(Boolean);
    if (!to.length) { showToast('Select at least one contact with an email address', 'warning'); return; }
    setEmailSending(true);
    try {
      await ProjectService.sendReportEmail(emailDialog.reportId, { to, body: emailBody });
      showToast('Report sent successfully', 'success');
      setEmailDialog(null);
    } catch { showToast('Failed to send email', 'error'); }
    finally { setEmailSending(false); }
  }

  const dateRange = [project.startDate, project.endDate].filter(Boolean);

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>

      {/* ── Header ── */}
      <Box sx={{ px: 2, pt: 2.5, pb: 2, bgcolor: '#fff', borderBottom: '1px solid #F1F5F9' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
          <IconButton size="small" onClick={onBack} sx={{ color: '#64748B', ml: -0.75 }}>
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0F172A', lineHeight: 1.2 }}>
              {project.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.35, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 12, color: '#64748B' }}>{project.customer?.name || '—'}</Typography>
              {dateRange.length > 0 && (
                <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
                  · {formatDate(project.startDate)}{project.endDate ? ` – ${formatDate(project.endDate)}` : ''}
                </Typography>
              )}
            </Box>
          </Box>
          <StatusPill statusMap={PROJECT_STATUS} value={project.status} />
        </Box>

        {project.description && (
          <Typography sx={{ fontSize: 12, color: '#64748B', pl: 4, mt: 0.25,
            overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {project.description}
          </Typography>
        )}
      </Box>

      {/* ── Toolbar: search + new report ── */}
      <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1.5, alignItems: 'center', bgcolor: '#fff', borderBottom: '1px solid #F1F5F9' }}>
        <TextField
          size="small" fullWidth
          placeholder="Search reports…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#94A3B8' }} /></InputAdornment> }}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 1.5 } }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={openNewReport}
          sx={{
            textTransform: 'none', fontWeight: 600, fontSize: 13,
            bgcolor: '#427898', '&:hover': { bgcolor: '#355f79' },
            borderRadius: 1.5, px: 2, py: 0.9, flexShrink: 0, whiteSpace: 'nowrap',
            boxShadow: '0 1px 2px rgba(66,120,152,0.25)',
          }}
        >
          New Report
        </Button>
      </Box>

      {/* ── Status filter chips ── */}
      <Box sx={{ px: 2, py: 1.25, display: 'flex', gap: 0.75, overflowX: 'auto', bgcolor: '#fff',
        borderBottom: '1px solid #F1F5F9', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        {STATUS_FILTERS.map(f => {
          const count = f.value === 'ALL' ? reports.length : (countByStatus[f.value] || 0);
          const active = statusFilter === f.value;
          return (
            <Box
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 1.25, py: 0.5, borderRadius: '20px', cursor: 'pointer', flexShrink: 0,
                fontSize: 12, fontWeight: active ? 600 : 500,
                color: active ? '#427898' : '#64748B',
                bgcolor: active ? '#EFF6FF' : 'transparent',
                border: `1px solid ${active ? '#BFDBFE' : 'transparent'}`,
                transition: 'all 0.12s',
                '&:hover': { bgcolor: active ? '#EFF6FF' : '#F8FAFC', color: active ? '#427898' : '#475569' },
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 'inherit', color: 'inherit', lineHeight: 1 }}>{f.label}</Typography>
              {count > 0 && (
                <Box sx={{ bgcolor: active ? '#BFDBFE' : '#E2E8F0', borderRadius: '10px', px: 0.6, minWidth: 16, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: active ? '#1D4ED8' : '#64748B', lineHeight: 1.6 }}>{count}</Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* ── Reports list ── */}
      <Box sx={{ px: 2, py: 2, bgcolor: '#F8FAFC', minHeight: 200 }}>
        {loadingReports ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} sx={{ color: '#427898' }} />
          </Box>
        ) : visibleReports.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
              <FolderOpenIcon sx={{ fontSize: 22, color: '#94A3B8' }} />
            </Box>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#475569', mb: 0.5 }}>
              {searchQuery || statusFilter !== 'ALL' ? 'No reports match your filters' : 'No reports yet'}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
              {searchQuery || statusFilter !== 'ALL' ? 'Try adjusting your search or filter' : 'Tap New Report to get started'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {visibleReports.map(r => (
              <ReportCard
                key={r.id}
                report={r}
                currentUserId={currentUser?.id}
                onResume={() => openEditReport(r)}
                onViewPdf={() => handleQuickViewPdf(r)}
                onDownloadPdf={() => handleDownloadPdf(r.id)}
                onEmail={() => openEmailDialog(r)}
                pdfViewLoading={pdfViewLoadingId === r.id}
                pdfDownloadLoading={pdfDownloadLoadingId === r.id}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* ── Drawer ── */}
      <ReportCompositionDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        project={project}
        editingReport={editingReport}
        fromField
        currentUserId={currentUser?.id}
        onSaved={saved => {
          setReports(prev => {
            const exists = prev.some(r => r.id === saved.id);
            return exists ? prev.map(r => r.id === saved.id ? { ...r, ...saved } : r) : [saved, ...prev];
          });
        }}
      />

      {/* ── PDF View Dialog ── */}
      <Dialog open={!!pdfViewUrl} onClose={closePdfView} fullWidth maxWidth="md"
        PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column', borderRadius: 2 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid #F1F5F9' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0F172A' }}>{pdfViewName}</Typography>
          <IconButton size="small" onClick={closePdfView}><CloseIcon sx={{ fontSize: 17 }} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, flex: 1, overflow: 'hidden' }}>
          {pdfViewUrl && <iframe src={pdfViewUrl} title="Report PDF" style={{ width: '100%', height: '100%', border: 'none', display: 'block' }} />}
        </DialogContent>
      </Dialog>

      {/* ── Email Dialog ── */}
      <Dialog open={!!emailDialog} onClose={() => setEmailDialog(null)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#0F172A' }}>Send Report by Email</Typography>
            {emailDialog?.displayName && <Typography sx={{ fontSize: 12, color: '#64748B', mt: 0.25 }}>{emailDialog.displayName}</Typography>}
          </Box>
          <IconButton size="small" onClick={() => setEmailDialog(null)}><CloseIcon sx={{ fontSize: 17 }} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
            Recipients
          </Typography>
          <Box sx={{ border: '1px solid #E2E8F0', borderRadius: 1.5, overflow: 'hidden', mb: 2 }}>
            {emailContactsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5 }}>
                <CircularProgress size={20} sx={{ color: '#427898' }} />
              </Box>
            ) : (
              <>
                {emailContacts.map((c, idx) => (
                  <Box key={c.id} onClick={() => c.email && toggleEmailContact(c.id)} sx={{
                    display: 'flex', alignItems: 'center', px: 2, py: 1.25,
                    borderBottom: idx < emailContacts.length - 1 || emailNewForm !== null ? '1px solid #F1F5F9' : 'none',
                    cursor: c.email ? 'pointer' : 'default',
                    bgcolor: emailSelectedIds.includes(c.id) ? '#EFF6FF' : 'transparent',
                    '&:hover': c.email ? { bgcolor: emailSelectedIds.includes(c.id) ? '#EFF6FF' : '#F8FAFC' } : {},
                  }}>
                    <Checkbox checked={emailSelectedIds.includes(c.id)} disabled={!c.email} size="small"
                      onChange={() => c.email && toggleEmailContact(c.id)}
                      onClick={e => e.stopPropagation()}
                      sx={{ p: 0, mr: 1.5, color: '#CBD5E1', '&.Mui-checked': { color: '#427898' } }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>{c.firstName} {c.lastName}</Typography>
                      <Typography sx={{ fontSize: 12, color: c.email ? '#64748B' : '#CBD5E1' }}>{c.email || 'No email address'}</Typography>
                    </Box>
                  </Box>
                ))}
                {emailNewForm !== null ? (
                  <Box sx={{ p: 2, bgcolor: '#FAFBFE' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#427898', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>New contact</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
                      <TextField size="small" label="First name *" value={emailNewForm.firstName || ''}
                        onChange={e => setEmailNewForm(p => ({ ...p, firstName: e.target.value }))}
                        error={!!emailNewErrors.firstName} helperText={emailNewErrors.firstName} />
                      <TextField size="small" label="Last name *" value={emailNewForm.lastName || ''}
                        onChange={e => setEmailNewForm(p => ({ ...p, lastName: e.target.value }))}
                        error={!!emailNewErrors.lastName} helperText={emailNewErrors.lastName} />
                    </Box>
                    <TextField fullWidth size="small" label="Email *" sx={{ mb: 1.5 }} value={emailNewForm.email || ''}
                      onChange={e => setEmailNewForm(p => ({ ...p, email: e.target.value }))}
                      error={!!emailNewErrors.email} helperText={emailNewErrors.email} />
                    <TextField fullWidth size="small" label="Phone *" sx={{ mb: 1.5 }} value={emailNewForm.phone || ''}
                      onChange={e => setEmailNewForm(p => ({ ...p, phone: e.target.value }))}
                      error={!!emailNewErrors.phone} helperText={emailNewErrors.phone} />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button size="small" onClick={() => { setEmailNewForm(null); setEmailNewErrors({}); }}
                        sx={{ textTransform: 'none', color: '#64748B', fontSize: 13 }}>Cancel</Button>
                      <Button size="small" variant="contained" onClick={handleAddEmailContact} disabled={emailNewSaving}
                        sx={{ textTransform: 'none', bgcolor: '#427898', fontSize: 13, '&:hover': { bgcolor: '#355f79' } }}>
                        {emailNewSaving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Save'}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box onClick={() => setEmailNewForm({ firstName: '', lastName: '', email: '', phone: '' })}
                    sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25, cursor: 'pointer', color: '#427898',
                      borderTop: emailContacts.length ? '1px solid #F1F5F9' : 'none',
                      '&:hover': { bgcolor: '#F8FAFC' } }}>
                    <AddIcon sx={{ fontSize: 15, mr: 1 }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Add new contact</Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Message</Typography>
          <TextField fullWidth size="small" multiline rows={4} value={emailBody} onChange={e => setEmailBody(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setEmailDialog(null)} sx={{ textTransform: 'none', color: '#64748B' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSendEmail} disabled={emailSending || emailSelectedIds.length === 0}
            startIcon={emailSending ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <EmailIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', bgcolor: '#427898', '&:hover': { bgcolor: '#355f79' } }}>
            Send{emailSelectedIds.length > 0 ? ` (${emailSelectedIds.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Project list ──────────────────────────────────────────────────────────────

export default function FieldReportsPage() {
  const { showToast } = useToast();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    Promise.all([
      ProjectService.getAllProjectsFlat(),
      UserService.getUserInfo(),
    ]).then(([data, user]) => {
      const active = data.filter(p => p.status !== 'COMPLETED' && p.status !== 'CANCELLED');
      setProjects(active);
      setCurrentUser(user);
    }).catch(() => showToast('Failed to load data', 'error'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const filtered = projects.filter(p => {
    const q = search.toLowerCase();
    return !q || p.name?.toLowerCase().includes(q) || p.customer?.name?.toLowerCase().includes(q);
  });

  if (selected) {
    return (
      <Layout>
        <ProjectPanel project={selected} onBack={() => setSelected(null)} currentUser={currentUser} />
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ maxWidth: 600, mx: 'auto', px: 2 }}>
        {/* Page header */}
        <Box sx={{ pt: 3, pb: 2.5 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#0F172A', mb: 0.5 }}>Field Reports</Typography>
          <Typography sx={{ fontSize: 13, color: '#64748B' }}>Select a project to create or view on-site reports.</Typography>
        </Box>

        {/* Search */}
        <TextField
          fullWidth size="small"
          placeholder="Search projects or customers…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#94A3B8' }} /></InputAdornment> }}
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 1.5 } }}
        />

        {/* Project list */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} sx={{ color: '#427898' }} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <FolderOpenIcon sx={{ fontSize: 26, color: '#94A3B8' }} />
            </Box>
            <Typography sx={{ fontSize: 15, fontWeight: 500, color: '#475569', mb: 0.5 }}>
              {search ? 'No matching projects' : 'No active projects'}
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>
              {search ? 'Try a different search term.' : 'You have no active projects assigned.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filtered.map(p => {
              const cfg = PROJECT_STATUS[p.status] || { color: '#64748B', bg: '#F1F5F9' };
              return (
                <Box
                  key={p.id}
                  onClick={() => setSelected(p)}
                  sx={{
                    bgcolor: '#fff', borderRadius: '10px', cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)',
                    transition: 'box-shadow 0.15s, transform 0.1s',
                    '&:hover': { boxShadow: '0 4px 16px rgba(15,23,42,0.09)', transform: 'translateY(-1px)' },
                    '&:active': { transform: 'translateY(0)' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.75, gap: 1.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.name}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#64748B', mt: 0.25 }}>
                        {p.customer?.name || '—'}
                      </Typography>
                    </Box>
                    <StatusPill statusMap={PROJECT_STATUS} value={p.status} size="sm" />
                    <ChevronRightIcon sx={{ fontSize: 16, color: '#CBD5E1', flexShrink: 0 }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Layout>
  );
}
